import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, matchPath, useLocation } from "react-router-dom";
import { Logo } from "@/components/brand/Logo";
import { GlobalSearch, SearchTrigger } from "@/components/search/GlobalSearch";
import { NotConfiguredBanner } from "@/components/ui/states";
import { cx } from "@/components/ui/primitives";
import { seasonOptions, useSeason } from "@/lib/season";
import { seasonLabel } from "@shared/nba-time";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { InstallButton, InstallInvite, OfflineBanner } from "@/components/pwa/InstallApp";

type NavItem = { to: string; label: string; end?: boolean; hint?: string };
type NavGroup = { label: string; items: NavItem[] };

/** Rubriques : accès direct aux plus consultées, les autres regroupées dans deux menus. */
export const NAV: Array<NavItem | NavGroup> = [
  { to: "/", label: "Accueil", end: true },
  { to: "/direct", label: "Direct" },
  { to: "/matchs", label: "Matchs" },
  { to: "/classements", label: "Classements" },
  { to: "/equipes", label: "Équipes" },
  { to: "/joueurs", label: "Joueurs" },
  {
    label: "Stats & records",
    items: [
      { to: "/statistiques", label: "Statistiques", hint: "Leaders, laboratoires joueurs et équipes" },
      { to: "/records", label: "Records & palmarès", hint: "Records, séries, distinctions" },
      { to: "/comparateur", label: "Comparateur", hint: "Joueurs ou équipes côte à côte" },
      { to: "/scouting", label: "Scouting", hint: "Rapports et grille d'évaluation" },
    ],
  },
  {
    label: "Histoire",
    items: [
      { to: "/histoire", label: "Histoire de la NBA", hint: "Thèmes, ères et tendances" },
      { to: "/saisons", label: "Archives des saisons", hint: "Chaque saison depuis 1946-47" },
    ],
  },
];

const isGroup = (n: NavItem | NavGroup): n is NavGroup => "items" in n;
const LINK_BASE = "relative flex items-center gap-1 whitespace-nowrap px-2.5 py-2.5 text-[0.82rem] font-semibold uppercase tracking-[0.04em] transition-colors lg:px-3";
const LINK_ACTIVE = "text-ink after:absolute after:inset-x-2.5 after:bottom-0 after:h-[3px] after:bg-accent lg:after:inset-x-3";

/** Menu déroulant de la barre de navigation : ouvert au clic, fermé par Échap, un clic extérieur ou un changement de page. */
function NavMenu({ group }: { group: NavGroup }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);
  const { pathname } = useLocation();
  const active = group.items.some((i) => matchPath({ path: i.to, end: false }, pathname));
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const id = `menu-${group.label.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <li ref={ref} className="relative">
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}
        className={cx(LINK_BASE, active ? LINK_ACTIVE : "text-muted hover:text-ink")}>
        {group.label}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden className={cx("transition-transform", open && "rotate-180")}><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <ul id={id} className="absolute left-0 top-full z-50 w-72 rounded-xl border border-line-strong bg-surface p-1.5 shadow-2xl shadow-black/30">
          {group.items.map((i) => (
            <li key={i.to}>
              <NavLink to={i.to} className={({ isActive }) => cx("block rounded-lg px-3 py-2.5 transition-colors", isActive ? "bg-accent-soft" : "hover:bg-surface-2")}>
                {({ isActive }) => (
                  <>
                    <span className={cx("block text-sm font-semibold", isActive ? "text-accent-text" : "text-ink")}>{i.label}</span>
                    {i.hint && <span className="mt-0.5 block text-xs text-muted">{i.hint}</span>}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function FavoritesLink({ className }: { className?: string }) {
  return (
    <NavLink to="/favoris" aria-label="Favoris" title="Favoris"
      className={({ isActive }) => cx("grid h-10 w-10 place-items-center rounded-lg border", isActive ? "border-accent text-accent-text" : "border-line-strong text-muted hover:text-ink", className)}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z" strokeLinejoin="round" /></svg>
    </NavLink>
  );
}

/** Icônes de la barre de navigation mobile. */
const TAB_ICONS: Record<string, ReactNode> = {
  "/": <path d="M4 11l8-7 8 7v9a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1z" strokeLinejoin="round" />,
  "/matchs": <><rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" strokeLinecap="round" /></>,
  "/classements": <path d="M5 20V10M12 20V4M19 20v-7" strokeLinecap="round" />,
  "/equipes": <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" strokeLinejoin="round" />,
  "/joueurs": <><circle cx="12" cy="8" r="3.5" /><path d="M5 20c.8-3.6 3.6-5.5 7-5.5s6.2 1.9 7 5.5" strokeLinecap="round" /></>,
};
const TABS: NavItem[] = [
  { to: "/", label: "Accueil", end: true },
  { to: "/matchs", label: "Matchs" },
  { to: "/classements", label: "Classement" },
  { to: "/equipes", label: "Équipes" },
  { to: "/joueurs", label: "Joueurs" },
];

function SeasonSelect({ compact }: { compact?: boolean }) {
  const { season, current, setSeason } = useSeason();
  return (
    <label className="flex items-center gap-2">
      <span className={cx("kicker", compact && "sr-only")}>Saison</span>
      <select
        value={season}
        onChange={(e) => setSeason(Number(e.target.value))}
        className={cx(
          "h-10 rounded-lg border bg-surface-2 px-2 font-mono text-sm",
          season === current ? "border-line-strong text-ink" : "border-accent text-accent-text",
        )}
        aria-label="Saison affichée"
      >
        {seasonOptions(current).map((y) => (
          <option key={y} value={y}>{seasonLabel(y)}{y === current ? " (en cours)" : ""}</option>
        ))}
      </select>
    </label>
  );
}

function ThemeToggle() {
  const [theme, toggle] = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      className="grid h-10 w-10 place-items-center rounded-lg border border-line-strong text-muted hover:text-ink"
      aria-label={theme === "dark" ? "Passer au thème clair" : "Passer au thème sombre"}
    >
      {theme === "dark" ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" strokeLinecap="round" /></svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" strokeLinejoin="round" /></svg>
      )}
    </button>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isCurrent, label, current, setSeason } = useSeason();
  const { isAdmin } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !typing)) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  const nav: Array<NavItem | NavGroup> = isAdmin ? [...NAV, { to: "/admin", label: "Administration" }] : NAV;
  const direct = nav.filter((n): n is NavItem => !isGroup(n));
  const groups = nav.filter(isGroup);

  return (
    <div className="flex min-h-dvh flex-col">
      <a href="#contenu" className="sr-only z-50 rounded bg-accent px-3 py-2 text-accent-ink focus:not-sr-only focus:fixed focus:left-3 focus:top-3">Aller au contenu</a>
      <NotConfiguredBanner />
      <header className="sticky top-0 z-40 border-b border-line bg-bg/90 pt-[env(safe-area-inset-top)] backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-3 px-4 sm:px-6 lg:px-8">
          <Logo />
          <div className="ml-auto hidden items-center gap-3 md:flex">
            <SearchTrigger onOpen={() => setSearchOpen(true)} />
            <SeasonSelect />
            <FavoritesLink />
            <InstallButton variant="icon" />
            <ThemeToggle />
          </div>
          <div className="ml-auto flex items-center gap-2 md:hidden">
            <button type="button" onClick={() => setSearchOpen(true)} className="grid h-10 w-10 place-items-center rounded-lg border border-line-strong text-muted" aria-label="Rechercher">
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" strokeLinecap="round" /></svg>
            </button>
            <button type="button" onClick={() => setMenuOpen((o) => !o)} aria-expanded={menuOpen} aria-controls="menu-mobile" className="grid h-10 w-10 place-items-center rounded-lg border border-line-strong text-muted" aria-label="Menu">
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="2">{menuOpen ? <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" /> : <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />}</svg>
            </button>
          </div>
        </div>
        <nav aria-label="Rubriques" className="hidden border-t border-line md:block">
          <ul className="mx-auto flex max-w-[1400px] gap-0.5 px-4 sm:px-6 lg:px-8">
            {nav.map((n) => isGroup(n) ? <NavMenu key={n.label} group={n} /> : (
              <li key={n.to}>
                <NavLink to={n.to} end={n.end} className={({ isActive }) => cx(LINK_BASE, isActive ? LINK_ACTIVE : "text-muted hover:text-ink")}>
                  {n.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        {menuOpen && (
          <nav id="menu-mobile" aria-label="Rubriques" className="border-t border-line bg-bg px-4 pb-4 md:hidden">
            <div className="flex items-center justify-between gap-3 py-3">
              <SeasonSelect compact />
              <div className="flex gap-2"><FavoritesLink /><ThemeToggle /></div>
            </div>
            {[{ label: "Rubriques", items: direct }, ...groups].map((g) => (
              <div key={g.label} className="mt-2 first:mt-0">
                {g.label !== "Rubriques" && <div className="kicker mb-1.5 mt-3">{g.label}</div>}
                <ul className="grid grid-cols-2 gap-1.5">
                  {g.items.map((n) => (
                    <li key={n.to}>
                      <NavLink to={n.to} end={n.end} className={({ isActive }) => cx("block rounded-lg border px-3 py-2.5 text-sm font-semibold", isActive ? "border-accent text-accent-text" : "border-line text-ink-2")}>
                        {n.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="mt-4"><InstallButton variant="row" onDone={() => setMenuOpen(false)} /></div>
            {/* Une fois connecté, l'administration apparaît parmi les rubriques ci-dessus */}
            {!isAdmin && (
              <Link to="/admin" className="mt-3 block text-center font-mono text-[0.68rem] uppercase tracking-wider text-faint hover:text-ink">Espace propriétaire · connexion</Link>
            )}
          </nav>
        )}
        <OfflineBanner />
        {!isCurrent && (
          <div className="border-t border-accent/30 bg-accent-soft">
            <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-2 px-4 py-1.5 text-sm sm:px-6 lg:px-8">
              <span><span className="kicker mr-2 text-accent-text">Voyage dans le temps</span>Vous consultez la saison <strong className="num">{label}</strong> : effectifs, classements et statistiques correspondent à cette saison.</span>
              <button type="button" onClick={() => setSeason(current)} className="font-mono text-xs uppercase tracking-wider text-accent-text underline underline-offset-2">Revenir à {seasonLabel(current)}</button>
            </div>
          </div>
        )}
      </header>

      <main id="contenu" className="mx-auto w-full max-w-[1400px] flex-1 px-4 pb-24 sm:px-6 md:pb-16 lg:px-8">
        {location.pathname === "/" && <InstallInvite />}
        {children}
      </main>

      <footer className="border-t border-line bg-bg-deep">
        <div className="mx-auto grid max-w-[1400px] gap-8 px-4 py-10 sm:px-6 md:grid-cols-[1.2fr_1fr_1fr] lg:px-8">
          <div>
            <Logo />
            <p className="mt-4 max-w-md text-sm text-muted">
              Encyclopédie NBA indépendante. Données sportives issues de sources identifiées, calculs dérivés documentés,
              analyses éditoriales signées. Aucune donnée n'est inventée : ce qui manque est indiqué comme tel.
            </p>
            <p className="mt-3 text-xs text-faint">
              Site non affilié à la NBA ni à ses franchises. Les noms, logos et marques cités appartiennent à leurs propriétaires ; les logos servent uniquement à identifier les équipes, dans le cadre d'un usage privé.
            </p>
          </div>
          <div>
            <div className="kicker mb-3">Intégrité</div>
            <ul className="space-y-2 text-sm">
              <li><Link className="text-ink-2 hover:text-accent-text" to="/methodologie">Méthodologie & définitions</Link></li>
              <li><Link className="text-ink-2 hover:text-accent-text" to="/methodologie#sources">Sources & droits</Link></li>
              <li><Link className="text-ink-2 hover:text-accent-text" to="/methodologie#fiabilite">Statuts de fiabilité</Link></li>
              <li><Link className="text-ink-2 hover:text-accent-text" to="/scouting/methode">Grille de scouting</Link></li>
            </ul>
          </div>
          <div>
            <div className="kicker mb-3">Fuseaux</div>
            <p className="text-sm text-muted">Horaires affichés à l'heure de Paris. Les matchs sont rattachés à leur date NBA (fuseau de New York), y compris lorsqu'ils se terminent après minuit en France.</p>
            {isAdmin ? null : <Link to="/admin" className="mt-4 inline-block font-mono text-[0.68rem] uppercase tracking-wider text-faint hover:text-ink">Espace propriétaire</Link>}
          </div>
        </div>
      </footer>

      <nav aria-label="Navigation rapide" className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-bg/95 backdrop-blur md:hidden">
        <ul className="grid grid-cols-5 pb-[env(safe-area-inset-bottom)]">
          {TABS.map((n) => (
            <li key={n.to}>
              <NavLink to={n.to} end={n.end} className={({ isActive }) => cx("flex h-14 flex-col items-center justify-center gap-1 text-[0.65rem] font-semibold uppercase tracking-wide", isActive ? "text-accent-text" : "text-muted")}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>{TAB_ICONS[n.to]}</svg>
                {n.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
